/**
 * EmailRecipientPicker — modal dialog used by Compressor Detail to send
 * a PM schedule (req #5) or a missing-parts list (req #6).
 *
 * Loads `Persons_for_Customer` rows from `/customers/{ownerId}/persons`
 * and presents them as a checkbox list. The user can:
 *   • toggle each contact individually
 *   • bulk select / deselect all
 *   • type one or more "other" addresses (comma-separated) that aren't
 *     in the contact table — same data path, no duplication.
 *
 * Also lets the user write an optional intro message (rendered above
 * the table in the email body) and toggle prices on/off.
 *
 * On submit, calls `onSubmit({ recipients, message, include_prices })`.
 * The parent owns the actual POST + result toast — this component just
 * collects the form state.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { X, EnvelopeSimple, Spinner } from "@phosphor-icons/react";
import { useT } from "@/lib/i18n";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function EmailRecipientPicker({
  open,
  onClose,
  onSubmit,
  user,
  ownerCustomerId,
  title,             // e.g. "Email PM Schedule"
  subtitle,          // e.g. "Send the maintenance plan to your dealer or fleet manager."
  defaultIncludePrices = true,
  busy = false,      // parent passes back true while the request is in-flight
  testIdPrefix = "email-picker",
}) {
  const t = useT();
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [extra, setExtra] = useState(""); // comma-separated free-form
  const [message, setMessage] = useState("");
  const [includePrices, setIncludePrices] = useState(defaultIncludePrices);

  useEffect(() => {
    if (!open || !ownerCustomerId) return;
    let cancelled = false;
    setLoading(true); setErr("");
    axios
      .get(`${API}/customers/${encodeURIComponent(ownerCustomerId)}/persons`, {
        withCredentials: true,
        headers: user?.email ? { "X-User-Email": user.email } : {},
      })
      .then((r) => {
        if (cancelled) return;
        const list = r.data?.persons || [];
        setPersons(list);
        // Pre-select primary + anyone flagged for ServiceAlerts.
        const pre = new Set();
        for (const p of list) {
          if ((p.IsPrimary === "Y" || p.ServiceAlerts === "Y") && p["Main Email"]) {
            pre.add(String(p["Main Email"]).trim().toLowerCase());
          }
        }
        setSelected(pre);
      })
      .catch((e) => {
        if (!cancelled) setErr(e?.response?.data?.detail || "Failed to load contacts");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, ownerCustomerId, user?.email]);

  const allEmails = useMemo(() => {
    const out = new Set(selected);
    extra.split(/[,;\n]/).forEach((e) => {
      const v = e.trim().toLowerCase();
      if (v) out.add(v);
    });
    return Array.from(out);
  }, [selected, extra]);

  if (!open) return null;

  const toggle = (email) => {
    const e = String(email || "").trim().toLowerCase();
    if (!e) return;
    const next = new Set(selected);
    if (next.has(e)) next.delete(e); else next.add(e);
    setSelected(next);
  };

  const allKnown = persons.filter((p) => p["Main Email"]);
  const allSelected = allKnown.length > 0 && allKnown.every((p) =>
    selected.has(String(p["Main Email"]).trim().toLowerCase())
  );

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allKnown.map((p) => String(p["Main Email"]).trim().toLowerCase())));
    }
  };

  const submit = () => {
    if (!allEmails.length) return;
    onSubmit({
      recipients: allEmails,
      message: message.trim(),
      include_prices: includePrices,
    });
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-3 bg-slate-900/60 backdrop-blur-sm" data-testid={`${testIdPrefix}-modal`}>
      <div className="bg-white rounded-md w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-start justify-between gap-3 p-4 border-b border-slate-200">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <EnvelopeSimple size={16} weight="fill" className="text-blue-700" />
              <h3 className="text-base font-black tracking-tight font-['Chivo'] truncate">{title}</h3>
            </div>
            {subtitle && <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1" data-testid={`${testIdPrefix}-close`} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading && (
            <div className="text-xs text-slate-500 flex items-center gap-2"><Spinner size={12} className="animate-spin" /> {t("common.loading", "Loading…")}</div>
          )}
          {err && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm p-2">{err}</div>
          )}

          {!loading && persons.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  {t("email_picker.contacts_label", "Company contacts")} ({allKnown.length})
                </label>
                <button type="button" onClick={toggleAll}
                  data-testid={`${testIdPrefix}-toggle-all`}
                  className="text-[10px] font-semibold text-blue-700 hover:underline">
                  {allSelected ? t("email_picker.deselect_all", "Deselect all") : t("email_picker.select_all", "Select all")}
                </button>
              </div>
              <ul className="border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-56 overflow-y-auto bg-white">
                {persons.map((p) => {
                  const email = String(p["Main Email"] || "").trim().toLowerCase();
                  if (!email) return null;
                  const checked = selected.has(email);
                  return (
                    <li key={p.PFCID || p.id || email}
                      className={`flex items-center gap-2 px-2.5 py-1.5 text-xs cursor-pointer ${checked ? "bg-blue-50/60" : "hover:bg-slate-50"}`}
                      onClick={() => toggle(email)}
                      data-testid={`${testIdPrefix}-row-${p.PFCID || email}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggle(email)}
                        className="accent-blue-600 cursor-pointer" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-800 truncate">{p.Name || "—"}</div>
                        <div className="text-[10px] text-slate-500 truncate">{email}</div>
                      </div>
                      {p.IsPrimary === "Y" && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm bg-emerald-100 text-emerald-700 border border-emerald-200">{t("email_picker.primary", "Primary")}</span>
                      )}
                      {p.ServiceAlerts === "Y" && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm bg-amber-100 text-amber-700 border border-amber-200">{t("email_picker.alerts", "Alerts")}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {!loading && persons.length === 0 && !err && (
            <div className="text-xs text-slate-500 italic">{t("email_picker.no_contacts", "No company contacts on file. Add one below.")}</div>
          )}

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
              {t("email_picker.other_label", "Other recipients")}
            </label>
            <input
              type="text"
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              data-testid={`${testIdPrefix}-extra`}
              placeholder={t("email_picker.other_placeholder", "name@example.com, manager@dealer.com")}
              className="w-full text-xs px-2 py-1.5 border border-slate-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <p className="text-[10px] text-slate-400 mt-0.5">{t("email_picker.other_hint", "Comma-separate to add multiple.")}</p>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
              {t("email_picker.message_label", "Optional message")}
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid={`${testIdPrefix}-message`}
              rows={2}
              placeholder={t("email_picker.message_placeholder", "Add a short note that will appear above the table.")}
              className="w-full text-xs px-2 py-1.5 border border-slate-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
            />
          </div>

          <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
            <input type="checkbox" checked={includePrices}
              data-testid={`${testIdPrefix}-include-prices`}
              onChange={(e) => setIncludePrices(e.target.checked)}
              className="accent-blue-600" />
            <span>{t("email_picker.include_prices", "Include list prices in the email")}</span>
          </label>
        </div>

        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-2">
          <span className="text-[11px] text-slate-500" data-testid={`${testIdPrefix}-count`}>
            {allEmails.length === 0
              ? t("email_picker.empty_state", "Pick at least one recipient")
              : t("email_picker.recipient_count", `${allEmails.length} recipient(s) selected`)}
          </span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose}
              data-testid={`${testIdPrefix}-cancel`}
              className="text-[11px] font-semibold uppercase tracking-wider px-3 h-8 rounded-sm border border-slate-200 text-slate-600 hover:bg-white">
              {t("common.cancel", "Cancel")}
            </button>
            <button type="button" onClick={submit}
              disabled={busy || allEmails.length === 0}
              data-testid={`${testIdPrefix}-send`}
              className="text-[11px] font-bold uppercase tracking-wider px-3 h-8 rounded-sm bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5">
              {busy ? <Spinner size={12} className="animate-spin" /> : <EnvelopeSimple size={12} weight="bold" />}
              {t("email_picker.send_button", "Send")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
